import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { PublicLoading, PublicError } from "@/pages/public/VisitorRegister";
import { toast } from "sonner";
import { Loader2, Check, PauseCircle, X, User, Building2, Phone, Target, MapPin, DoorOpen } from "lucide-react";

export default function EmployeeApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [acting, setActing] = useState(null);
  const [result, setResult] = useState(null);

  const load = () => axios.get(`${API_BASE}/public/visitor/approval/${token}`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, [token]);

  const act = async (action) => {
    setActing(action);
    try {
      const { data: res } = await axios.post(`${API_BASE}/public/visitor/approval/${token}`, { action });
      setResult({ action, ...res });
      toast.success(`Visitor ${res.result}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setActing(null); }
  };

  if (err) return <PublicError title="Link Unavailable" message={err} />;
  if (!data) return <PublicLoading />;

  const rows = [
    { icon: User, label: "Visitor", value: data.visitor_name },
    { icon: Building2, label: "Company", value: data.visitor_company },
    { icon: Phone, label: "Phone", value: data.visitor_phone },
    { icon: Target, label: "Purpose", value: data.purpose },
    { icon: User, label: "Person to Meet", value: data.person_to_meet },
    { icon: MapPin, label: "Branch", value: `${data.company} · ${data.branch}` },
  ];

  const decided = data.decided || result;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sf-grid-bg px-6 py-8 text-white"><Logo className="h-7" light /></div>
      <div className="max-w-lg mx-auto px-4 -mt-6">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden sf-fade-up">
          <div className="p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Visitor Approval Request</p>
            <h1 className="font-display text-2xl font-bold text-navy-900 mt-1">Do you approve this visitor?</h1>
            <div className="mt-6 divide-y divide-slate-100 border border-slate-100 rounded-2xl">
              {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                  <r.icon size={17} className="text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-500 w-32 flex-shrink-0">{r.label}</span>
                  <span className="text-sm font-semibold text-navy-900">{r.value}</span>
                </div>
              ))}
            </div>

            {decided ? (
              <div className="mt-6 text-center p-5 rounded-2xl bg-slate-50 border border-slate-200">
                <StatusBadge status={result?.status || data.status} />
                <p className="text-sm text-slate-500 mt-3">
                  {result?.result === "approved" || (data.status || "").includes("PENDING_RECEPTION")
                    ? "Approved. A visitor pass has been generated and reception has been notified."
                    : "This request has been recorded. Thank you."}
                </p>
                {result?.pass_link && (
                  <a href={result.pass_link} className="text-blue-600 text-sm font-medium mt-2 inline-block" data-testid="pass-link">View Visitor Pass →</a>
                )}
              </div>
            ) : (
              <>
              <p className="text-sm text-slate-500 mt-6">Review the visitor details above and choose an action. Once approved, a visitor pass is emailed to the visitor and reception is notified.</p>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Button onClick={() => act("approve")} disabled={!!acting} data-testid="approve-btn" className="h-14 flex-col gap-1 bg-emerald-600 hover:bg-emerald-700">
                  {acting === "approve" ? <Loader2 className="animate-spin" size={18} /> : <><Check size={18} /><span className="text-xs">Approve</span></>}
                </Button>
                <Button onClick={() => act("hold")} disabled={!!acting} data-testid="hold-btn" variant="outline" className="h-14 flex-col gap-1 border-amber-300 text-amber-700 hover:bg-amber-50">
                  {acting === "hold" ? <Loader2 className="animate-spin" size={18} /> : <><PauseCircle size={18} /><span className="text-xs">Hold</span></>}
                </Button>
                <Button onClick={() => act("reject")} disabled={!!acting} data-testid="reject-btn" variant="outline" className="h-14 flex-col gap-1 border-rose-300 text-rose-700 hover:bg-rose-50">
                  {acting === "reject" ? <Loader2 className="animate-spin" size={18} /> : <><X size={18} /><span className="text-xs">Reject</span></>}
                </Button>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
